import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, NgForm, ReactiveFormsModule, Validators } from '@angular/forms';

import { KeyValueData } from '../../../domains/common.domain';
import { InstallationFlowType } from '../../../constants/app.constants';
import { LocalStorageService } from '../../../services/local-storage.service';
import { ValidationsService } from '../../../services/validations.service';

import { PersonalDataService } from '../personal-data.service';
import { DocumentResponse, LookupOptionsData, Document } from '../../jurisdiction.domain';
import { AllDropdownKeys } from '../../jurisdiction.constants';
import { CommonService } from '../../../services/common.service';
import { FileService } from '../../../services/file.service';
import { ErrorService } from '../../../services/error/error.service';
import { EnvironmentService } from '../../../services/environment.service';
import { distinctUntilChanged } from 'rxjs/operators';
import { GridApplicationDocumentTypes } from 'src/app/+grid-application-shared/entities/enums/grid-application-document-types.enum';
import { DocumentAndInputName } from 'src/app/+shared/models/documentAndInputName';
import { SectionIdentifier } from 'src/app/+shared/components/review-section/review-section.domain';
import { hideButtonsForChimneySweeper } from '../../../../app/+shared/components/review-section/review-section.component';

@Component({
  selector: 'app-subscriber',
  styleUrls: ['./subscriber.component.scss'],
  templateUrl: './subscriber.component.html',
})
export class SubscriberComponent implements OnInit {
  @Input() connectionData: any;
  @Input() subscriberData: any;
  @Input() isReadOnly: boolean = false;
  @Input() set documents(value: Document[]) {
    if (!value?.length) return;
    const lastDocument = value
      .filter(d => d.sk_document_type === GridApplicationDocumentTypes.PowerOfAttorney)
      .reduce((prev, curr) => {
        if (!prev || curr.document_id > prev.document_id) return curr;

        return prev;
      }, null);

    this.landownerDocument = lastDocument || new Document(GridApplicationDocumentTypes.PowerOfAttorney);
    if (lastDocument && this.subscriberDataFG?.controls?.landownerDocumentId) {
      this.subscriberDataFG.controls.landownerDocumentId.setValue(lastDocument.document_id);
    }
  }
  @Input() isDetailsView: boolean = false;

  addParcelAddress: boolean;
  allTitles: LookupOptionsData[] = this.commonService.getDropdownData(AllDropdownKeys.Title);
  currentInstallationFlow: string = this.localStorageService.getItem('installation_flow') || '';
  infoBubblesData: {
    [key: string]: boolean;
  };
  installationFlowType: KeyValueData = InstallationFlowType;
  documentTypes = GridApplicationDocumentTypes;
  locationData: any;
  subscriberDataFG: FormGroup;
  titleKey: string = AllDropdownKeys.Title;
  apartmentHouseType: string;
  personalData: any;
  telephoneEmailRequired: boolean;
  repeatEmailRequired: boolean = false;
  landownerDocument = new Document('landownerDocument');

  //show/hide flags
  showMessageForAddress: boolean = false;
  showConnectionUser: boolean = false;
  showLandownerWarning: boolean = false;
  showEmailInsteadOfMobile: boolean = false;
  showLandownerSwitch: boolean = false;
  showInvoiceRecepientSwitch: boolean = false;
  applicationId: number;

  ConnectionUserSectionIdentifier = SectionIdentifier.PersonalData_ConnectionUser;
  hideButtonsForChimneySweeper = hideButtonsForChimneySweeper;

  constructor(
    private commonService: CommonService,
    private environmentService: EnvironmentService,
    private localStorageService: LocalStorageService,
    private personalDataService: PersonalDataService,
    private fileService: FileService,
    private errorService: ErrorService
  ) {
    const locationData = this.localStorageService.getItem('location_data');

    this.apartmentHouseType = (locationData && !this.isReadOnly && locationData.apartmentHouse) || '';
    this.landownerDocument.name = 'landownerDocument';
  }

  ngOnInit(): void {
    this.applicationId = parseInt(this.localStorageService.getItem('application_id'));
    this.personalData = this.localStorageService.getItem('personal_data');
    this.showConnectionUser = this.environmentService.showConnectionUser;
    this.showEmailInsteadOfMobile = this.environmentService.showEmailInsteadOfMobile;
    this.showLandownerSwitch = this.environmentService.showLandownerPerApplication(this.currentInstallationFlow);
    this.showInvoiceRecepientSwitch = this.environmentService.showInvoiceAddressPerApplication(this.currentInstallationFlow);
    this.infoBubblesData = this.environmentService.getInfoBubblesData('personalData') || null;
    this.telephoneEmailRequired = this.environmentService.telephoneEmailRequired;
    this.locationData = this.localStorageService.getItem('location_data') || {};
    this.addParcelAddress = this.locationData?.noAddressAvailable || (this.isReadOnly && this.connectionData?.swsParcel) || false;
    this.prepareForm();
    this.useSameAddress(true);

    this.personalDataService.updatePersonalDataForm(this.subscriberDataFG, 'subscriber');
  }

  handleLandownerDocumentSection(): void {
    if (this.subscriberDataFG.get('landowner').value) {
      this.showLandownerWarning = false;
      this.subscriberDataFG.removeControl('landownerDocumentName');
      this.subscriberDataFG.removeControl('landownerDocumentId');
      this.subscriberDataFG.removeControl('landownerDocument');

      if (this.landownerDocument.document_id) {
        this.deleteDocument(this.landownerDocument.document_id);
      }

      return;
    }

    this.showLandownerWarning = this.environmentService.showLandownerHintText;
    this.subscriberDataFG.addControl(
      'landownerDocumentName',
      new FormControl(this.landownerDocument.filename || '', [Validators.required])
    );
    this.subscriberDataFG.addControl(
      'landownerDocumentId',
      new FormControl(this.landownerDocument.document_id || '', [Validators.required])
    );
    this.subscriberDataFG.addControl('landownerDocument', new FormControl(this.landownerDocument, [Validators.required]));

    if (
      this.personalData &&
      this.personalData.subscriberData &&
      this.personalData.subscriberData.landownerDocumentId &&
      this.personalData.subscriberData.landownerDocumentName
    ) {
      this.subscriberDataFG.controls.landownerDocumentName.setValue(this.personalData.subscriberData.landownerDocumentName);
      this.subscriberDataFG.controls.landownerDocumentId.setValue(this.personalData.subscriberData.landownerDocumentId);
    }
  }

  useSameAddress(isFirst: boolean, connectionFormData?: FormGroup): void {
    const isTrue = isFirst ? this.connectionData.infoCheckbox : connectionFormData && this.subscriberDataFG.controls.infoCheckbox.value;
    const connectionDataValue = isFirst ? this.connectionData : this.personalDataService.getFormValues(connectionFormData);
    Object.keys(this.subscriberDataFG.controls).forEach(item => {
      if (connectionDataValue.hasOwnProperty(item)) {
        if (isTrue) {
          this.subscriberDataFG.controls[item].setValue(connectionDataValue[item]);
          if (!this.isReadOnly) {
            this.subscriberDataFG.controls[item].disable();
          }
        } else {
          if (!this.isReadOnly) {
            this.subscriberDataFG.controls[item].enable();
          }
        }
      }
    });
  }

  useSameAddressAsConnection(isChecked: any): void {
    const connectionDataValue = this.personalDataService.getFormValues(this.personalDataService.connectionAddress);

    if (isChecked?.checked) {
      ['city', 'postCode', 'street', 'streetNo'].every(item => {
        if (connectionDataValue[item]) {
          this.showMessageForAddress = false;
        } else {
          this.subscriberDataFG.controls.infoCheckbox.setValue(false);
          this.showMessageForAddress = true;
          return false;
        }
        return true;
      });
    }

    if (!this.showMessageForAddress) {
      Object.keys(this.subscriberDataFG.controls).forEach(item => {
        if (connectionDataValue.hasOwnProperty(item)) {
          if (isChecked?.checked) {
            this.subscriberDataFG.controls[item].setValue(connectionDataValue[item]);
            if (!this.isReadOnly) {
              this.subscriberDataFG.controls[item].disable();
            }
          } else {
            if (!this.isReadOnly) {
              this.subscriberDataFG.controls[item].enable();
            }
          }
        }
      });
    }
  }

  deleteDocument(documentId: string) {
    this.fileService.removeWorkflowDocument(documentId, { skipHistoryLog: true }).subscribe(
      response => {
        this.landownerDocument = new Document(GridApplicationDocumentTypes.PowerOfAttorney);
        this.subscriberDataFG.controls.landownerDocumentName?.setValue('');
        this.subscriberDataFG.controls.landownerDocumentId?.setValue('');
        this.errorService.throwErrorIfFailure(response, true);
      },
      error => {
        this.errorService.throwErrorIfFailure(error, true);
      }
    );
  }

  updateFile(event: DocumentAndInputName) {
    if (!event.document?.file) return; // delete operation

    const existingDocumentId = this.landownerDocument.document_id;

    this.fileService
      .uploadSingleDocument(
        event.document.file,
        this.applicationId.toString(),
        GridApplicationDocumentTypes.PowerOfAttorney,
        event.document.filename
      )
      .subscribe(
        async (response: DocumentResponse) => {
          this.subscriberDataFG.controls.landownerDocumentName.setValue(event.document.filename);
          const documentId = response.document[0].document_id;
          this.subscriberDataFG.controls.landownerDocumentId.setValue(documentId);
          this.errorService.throwErrorIfFailure(response, true);
          this.landownerDocument = {
            ...this.landownerDocument,
            ...response.document,
            document_id: response.document[0].document_id,
          };

          if (existingDocumentId) {
            await this.fileService.removeWorkflowDocument(existingDocumentId).toPromise();
          }
        },
        error => {
          this.errorService.throwErrorIfFailure(error, true);
        }
      );
  }

  private prepareForm(): void {
    let email = '';
    if (this.environmentService.presetEmail) {
      email = this.localStorageService.getItem('user_email');
    }
    const defaultStreetNumber = this.environmentService.defaultStreetNumber;
    this.subscriberDataFG = new FormGroup(
      {
        title: new FormControl(this.subscriberData.title || '', [Validators.required]),
        // customerNo: new FormControl(this.subscriberData.customerNo || '', [Validators.required]),
        firstName: new FormControl(this.subscriberData.firstName || '', [Validators.required]),
        lastName: new FormControl(this.subscriberData.lastName || '', [Validators.required]),
        company: new FormControl(this.subscriberData.company || ''),
        // infoCheckbox: new FormControl(this.subscriberData.infoCheckbox || false),
        street: new FormControl(this.subscriberData.street || '', [Validators.required]),
        streetNo: new FormControl(this.subscriberData.streetNo || defaultStreetNumber, [
          Validators.required,
          ValidationsService.streetNumberValidator(),
        ]),
        addressSuffix: new FormControl(this.subscriberData.addressSuffix || ''),
        city: new FormControl(this.subscriberData.city || '', [Validators.required]),
        postCode: new FormControl(this.subscriberData.postCode || '', [Validators.required, ValidationsService.maxLengthValidator()]),
        telephone: new FormControl(this.subscriberData.telephone || ''),
        // mobile: new FormControl(this.subscriberData.mobile || ''),
        email: new FormControl(this.subscriberData.email || email),
        repeatEmail: new FormControl(this.subscriberData.email || ''),
        infoCheckbox: new FormControl(this.subscriberData.infoCheckbox || false),
        con_user_firstname: new FormControl(this.subscriberData.con_user_firstname || ''),
        con_user_lastname: new FormControl(this.subscriberData.con_user_lastname || ''),
        con_user_company: new FormControl(this.subscriberData.con_user_company || ''),
        con_user_phone: new FormControl(this.subscriberData.con_user_phone || ''),
      },
      {
        validators: [ValidationsService.stringComparisonValidator('email', 'repeatEmail')],
      }
    );
    if (!this.showEmailInsteadOfMobile) {
      this.subscriberDataFG.addControl('mobile', new FormControl(this.subscriberData.mobile || ''));
    }
    if (this.telephoneEmailRequired) {
      this.subscriberDataFG.get('telephone').setValidators([Validators.required]);
      this.subscriberDataFG.get('email').setValidators([Validators.required, ValidationsService.emailValidator()]);
      this.subscriberDataFG.get('repeatEmail').setValidators([Validators.required, ValidationsService.emailValidator()]);
    } else {
      if (this.subscriberDataFG.value.email) {
        this.repeatEmailRequired = true;
      }
      this.subscriberDataFG
        .get('email')
        .valueChanges.pipe(distinctUntilChanged())
        .subscribe(emailValue => {
          if (emailValue) {
            this.subscriberDataFG.get('email').setValidators([ValidationsService.emailValidator()]);
            this.subscriberDataFG.get('repeatEmail').setValidators([Validators.required, ValidationsService.emailValidator()]);
            this.repeatEmailRequired = true;
          } else {
            this.subscriberDataFG.get('email').clearValidators();
            this.subscriberDataFG.get('email').updateValueAndValidity();
            this.subscriberDataFG.get('repeatEmail').clearValidators();
            this.subscriberDataFG.get('repeatEmail').updateValueAndValidity();
            this.repeatEmailRequired = false;
          }
        });
    }

    if (this.showLandownerSwitch) {
      this.subscriberDataFG.addControl('land', new FormControl(this.subscriberData.land || 'Deutschland', [Validators.required]));
      this.subscriberDataFG.addControl('landowner', new FormControl(this.subscriberData.landowner || false, [Validators.required]));
      this.handleLandownerDocumentSection();
    }

    if (this.showInvoiceRecepientSwitch) {
      this.subscriberDataFG.addControl(
        'invoiceRecipient',
        new FormControl(this.subscriberData.invoiceRecipient || false, [Validators.required])
      );
    }

    this.subscriberDataFG.valueChanges.subscribe(values => {
      if (values.infoCheckbox) {
        this.personalDataService.reuseSubscriberData(this.subscriberDataFG);
      }
      this.personalDataService.updatePersonalDataForm(this.subscriberDataFG, 'subscriber');
    });
  }
}
